'use client';

import Script from 'next/script';
import { useCallback, useEffect, useRef } from 'react';

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      language: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileProps = {
  siteKey: string;
  locale: string;
  /** Bump to get a new token: a token works only once. */
  resetKey: number;
  onToken: (token: string) => void;
};

export function Turnstile({
  siteKey,
  locale,
  resetKey,
  onToken,
}: TurnstileProps) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);

  const mount = useCallback(() => {
    if (!container.current || !window.turnstile || widget.current) return;
    widget.current = window.turnstile.render(container.current, {
      sitekey: siteKey,
      language: locale,
      callback: onToken,
      'expired-callback': () => onToken(''),
      'error-callback': () => onToken(''),
    });
  }, [locale, onToken, siteKey]);

  useEffect(mount, [mount]);

  useEffect(() => {
    if (resetKey > 0 && widget.current) {
      window.turnstile?.reset(widget.current);
    }
  }, [resetKey]);

  return (
    <>
      <Script src={SCRIPT_URL} strategy="afterInteractive" onReady={mount} />
      <div ref={container} data-testid="turnstile" />
    </>
  );
}
